import apiClient from '../../shared/services/apiClient';

/**
 * 注册商管理服务（Admin）
 * 用于管理域名注册商配置、TLD价格和用户域名
 */
export const registrarService = {
    // ==========================================
    // 注册商插件管理
    // ==========================================

    /**
     * 获取所有可用的注册商插件
     * @returns {Promise} 插件列表
     */
    getPlugins: async () => {
        try {
            const response = await apiClient.get('/api/admin/domain/registrars/plugins');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // 注册商配置管理
    // ==========================================

    /**
     * 获取注册商列表
     * @param {Object} params - 查询参数
     * @returns {Promise} 注册商列表
     */
    getRegistrars: async (params = {}) => {
        try {
            const response = await apiClient.get('/api/admin/domain/registrars', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取单个注册商详情
     * @param {number} id - 注册商ID
     * @returns {Promise} 注册商详细信息
     */
    getRegistrar: async (id) => {
        try {
            const response = await apiClient.get(`/api/admin/domain/registrars/get?id=${id}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 创建注册商
     * @param {Object} data - 注册商数据
     * @returns {Promise} 创建结果
     */
    createRegistrar: async (data) => {
        try {
            const response = await apiClient.post('/api/admin/domain/registrars/create', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 更新注册商
     * @param {Object} data - 注册商数据（包含id）
     * @returns {Promise} 更新结果
     */
    updateRegistrar: async (data) => {
        try {
            const response = await apiClient.post('/api/admin/domain/registrars/update', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 删除注册商
     * @param {number} id - 注册商ID
     * @param {boolean} force - 是否强制删除
     * @returns {Promise} 删除结果
     */
    deleteRegistrar: async (id, force = false) => {
        try {
            const response = await apiClient.delete('/api/admin/domain/registrars/delete', {
                data: { id, force }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 测试注册商连接
     * @param {string} pluginName - 插件名称
     * @param {Object} credentials - API凭证
     * @returns {Promise} 测试结果
     */
    testConnection: async (pluginName, credentials, sandboxMode = false, registrarId = null) => {
        try {
            // registrarId: 编辑已保存注册商时传入,后端用库里原值补齐掩码字段
            const response = await apiClient.post('/api/admin/domain/registrars/test-connection', {
                ...(registrarId ? { id: registrarId } : {}),
                plugin_name: pluginName,
                credentials,
                sandbox_mode: sandboxMode
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 同步注册商TLD价格
     * @param {number} id - 注册商ID
     * @returns {Promise} 同步结果
     */
    syncPricing: async (id) => {
        try {
            const response = await apiClient.post('/api/admin/domain/registrars/sync-pricing', { id });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // TLD价格管理
    // ==========================================

    /**
     * 获取TLD价格列表
     * @param {Object} params - 查询参数
     * @returns {Promise} TLD价格列表
     */
    // 隐私模式（代理注册）
    getPrivacySettings: async () => {
        try { return (await apiClient.get('/api/admin/domain/privacy/settings')).data; } catch (error) { throw error.response?.data || error; }
    },
    savePrivacySettings: async (data) => {
        try { return (await apiClient.post('/api/admin/domain/privacy/settings', data)).data; } catch (error) { throw error.response?.data || error; }
    },
    // 2026-10-21 定价模式（自动/手动）+ 亏本保护
    setPricingMode: async (ids, mode) => {
        try { return (await apiClient.post('/api/admin/domain/pricing/mode', { ids, mode })).data; } catch (error) { throw error.response?.data || error; }
    },
    setManualPrices: async (data) => {
        try { return (await apiClient.post('/api/admin/domain/pricing/manual-prices', data)).data; } catch (error) { throw error.response?.data || error; }
    },
    reenableTld: async (id) => {
        try { return (await apiClient.post('/api/admin/domain/pricing/reenable', { id })).data; } catch (error) { throw error.response?.data || error; }
    },
    setPrivacyRowSupport: async (data) => {
        try { return (await apiClient.post('/api/admin/domain/privacy/row-support', data)).data; } catch (error) { throw error.response?.data || error; }
    },
    disclosePrivacy: async (data) => {
        try { return (await apiClient.post('/api/admin/domain/privacy/disclose', data)).data; } catch (error) { throw error.response?.data || error; }
    },
    privacyAbuseNote: async (data) => {
        try { return (await apiClient.post('/api/admin/domain/privacy/abuse-note', data)).data; } catch (error) { throw error.response?.data || error; }
    },
    getPrivacyLog: async (params = {}) => {
        try { return (await apiClient.get('/api/admin/domain/privacy/log', { params })).data; } catch (error) { throw error.response?.data || error; }
    },

    getTldPricings: async (params = {}) => {
        try {
            const response = await apiClient.get('/api/admin/domain/registrars/tlds', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取单个TLD元数据（调用插件内部方法）
     * @param {number} registrarId
     * @param {string} tld
     */
    getTldMeta: async (registrarId, tld) => {
        try {
            const response = await apiClient.get('/api/admin/domain/registrars/tld-meta', {
                params: { registrar_id: registrarId, tld }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 更新TLD价格
     * @param {Object} data - TLD价格数据（包含id）
     * @returns {Promise} 更新结果
     */
    updateTldPricing: async (data) => {
        try {
            const response = await apiClient.post('/api/admin/domain/registrars/tlds/update', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 仅启用给定TLD的当前注册商，禁用其他注册商
     * @param {number} id - TLD定价ID
     * @returns {Promise} 状态对照数据
     */
    forceExclusiveTld: async (payload) => {
        try {
            const response = await apiClient.post('/api/admin/domain/registrars/tlds/force-exclusive', payload);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 批量仅启用给定列表中每个TLD的一条记录
     * @param {Array<number>} ids - TLD定价ID列表
     * @returns {Promise} 状态对照数据
     */
    forceExclusiveTldBulk: async (payload = {}) => {
        try {
            const response = await apiClient.post('/api/admin/domain/registrars/tlds/force-exclusive-bulk', payload);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // 用户域名管理（Admin）
    // ==========================================

    /**
     * 获取用户域名列表（Admin）
     * @param {Object} params - 查询参数
     * @returns {Promise} 域名列表
     */
    getUserDomains: async (params = {}) => {
        try {
            const response = await apiClient.get('/api/admin/domain', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取用户域名详情（Admin）
     * @param {number} id - 域名ID
     * @returns {Promise} 域名详细信息
     */
    getUserDomain: async (id) => {
        try {
            const response = await apiClient.get(`/api/admin/domain-registration/get?id=${id}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 从注册商同步已有域名（Admin）
     * @param {number} registrarId - 注册商ID
     * @param {Object} [filters] - 传给注册商的过滤条件
     * @returns {Promise} 同步结果 { code, data: { total, synced, errors } }
     */
    syncDomainsFromRegistrar: async (registrarId, filters = {}) => {
        try {
            const response = await apiClient.post('/api/admin/domains/sync-from-registrar', {
                registrar_id: registrarId,
                filters
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 批量导入域名（Admin）
     * @param {number} registrarId - 注册商ID
     * @param {Array} domains - [{ domain, user_id, ... }]
     * @returns {Promise} 导入结果 { code, data: { total, imported, errors } }
     */
    batchImportDomains: async (registrarId, domains) => {
        try {
            const response = await apiClient.post('/api/admin/domains/batch-import', {
                registrar_id: registrarId,
                domains
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取域名统计信息（Admin）
     * @param {number} [registrarId] - 可选注册商筛选
     * @returns {Promise} 统计信息
     */
    getDomainStatistics: async (registrarId = null) => {
        try {
            const params = registrarId ? { registrar_id: registrarId } : {};
            const response = await apiClient.get('/api/admin/domains/statistics', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取处于宽限/赎回期的域名（Admin）
     * @param {Object} [params] - { period: 'grace'|'redemption', registrar_id, page, limit }
     * @returns {Promise} { code, data: { list, total } }
     */
    getGraceRedemptionDomains: async (params = {}) => {
        try {
            const response = await apiClient.get('/api/admin/domains/grace-redemption', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 恢复处于赎回期的域名（Admin）
     * @param {number} domainId - 域名ID
     * @returns {Promise} 恢复结果
     */
    restoreDomain: async (domainId) => {
        try {
            const response = await apiClient.post('/api/admin/domains/restore', { domain_id: domainId });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 创建域名记录（Admin）
     * @param {Object} data - 域名数据
     * @returns {Promise} 创建结果
     */
    createUserDomain: async (data) => {
        try {
            const response = await apiClient.post('/api/admin/domains/create', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 更新域名记录（Admin）
     * @param {Object} data - 域名数据（包含id）
     * @returns {Promise} 更新结果
     */
    updateUserDomain: async (data) => {
        try {
            const response = await apiClient.post('/api/admin/domains/update', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 删除域名记录（Admin）
     * @param {number} id - 域名ID
     * @returns {Promise} 删除结果
     */
    deleteUserDomain: async (id) => {
        try {
            const response = await apiClient.delete('/api/admin/domains/delete', {
                data: { id }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取域名详情（Admin）- 用于详情页
     * @param {number} id - 域名ID
     * @returns {Promise} 域名详细信息
     */
    getDomainDetail: async (id) => {
        try {
            const response = await apiClient.get('/api/admin/domain-registration/get', {
                params: { id }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // 胶水记录（Glue Records）管理 - Admin
    // ==========================================

    /**
     * 获取域名的胶水记录列表
     * @param {number} domainId - 域名ID
     */
    getGlueRecords: async (domainId) => {
        try {
            const response = await apiClient.get('/api/admin/domain-registration/glue-records', {
                params: { domain_id: domainId }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 添加胶水记录
     */
    addGlueRecord: async (domainId, hostname, ipv4, ipv6) => {
        try {
            const response = await apiClient.post('/api/admin/domain-registration/glue-records/add', {
                domain_id: domainId,
                hostname,
                ipv4,
                ipv6
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 更新胶水记录
     */
    updateGlueRecord: async (domainId, hostname, ipv4, ipv6) => {
        try {
            const response = await apiClient.post('/api/admin/domain-registration/glue-records/update', {
                domain_id: domainId,
                hostname,
                ipv4,
                ipv6
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 删除胶水记录
     */
    deleteGlueRecord: async (domainId, hostname) => {
        try {
            const response = await apiClient.post('/api/admin/domain-registration/glue-records/delete', {
                domain_id: domainId,
                hostname
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // DNSSEC 管理 - Admin
    // ==========================================

    /**
     * 获取域名的DNSSEC记录
     * @param {number} domainId - 域名ID
     */
    getDnssec: async (domainId) => {
        try {
            const response = await apiClient.get('/api/admin/domain-registration/dnssec', {
                params: { domain_id: domainId }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 添加DNSSEC DS记录
     */
    addDnssec: async (domainId, dsRecord) => {
        try {
            const response = await apiClient.post('/api/admin/domain-registration/dnssec/add', {
                domain_id: domainId,
                ...dsRecord
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 删除DNSSEC DS记录
     */
    deleteDnssec: async (domainId, index) => {
        try {
            const response = await apiClient.post('/api/admin/domain-registration/dnssec/delete', {
                domain_id: domainId,
                index
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 更新域名DNS服务器（Admin）
     * @param {number} id - 域名ID
     * @param {Array} nameservers - DNS服务器列表
     * @returns {Promise} 更新结果
     */
    updateNameservers: async (id, nameservers) => {
        try {
            const response = await apiClient.post('/api/admin/domain/update-nameservers', {
                id,
                nameservers
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 切换域名锁定状态（Admin）
     * @param {number} id - 域名ID
     * @param {boolean} lock - 是否锁定
     * @returns {Promise} 操作结果
     */
    toggleDomainLock: async (id, lock) => {
        try {
            const response = await apiClient.post('/api/admin/domain/toggle-lock', {
                id,
                lock
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 切换WHOIS隐私保护（Admin）
     * @param {number} id - 域名ID
     * @param {boolean} enable - 是否启用
     * @returns {Promise} 操作结果
     */
    toggleWhoisPrivacy: async (id, enable) => {
        try {
            const response = await apiClient.post('/api/admin/domain/toggle-privacy', {
                id,
                enable
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /** 自动续费开关（Admin，本地：域名 + 订阅） */
    toggleAutoRenew: async (id, autoRenew) => {
        try {
            const response = await apiClient.post('/api/admin/domain/toggle-auto-renew', { id, auto_renew: autoRenew });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取域名转出码（Admin）
     * @param {number} id - 域名ID
     * @returns {Promise} 转出码
     */
    getAuthCode: async (id) => {
        try {
            const response = await apiClient.get(`/api/admin/domain/get-auth-code?id=${id}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 更新域名联系人信息（Admin）
     * @param {number} id - 域名ID
     * @param {Object} contacts - 联系人信息
     * @returns {Promise} 更新结果
     */
    updateContacts: async (id, contacts, templateIds = {}) => {
        try {
            const response = await apiClient.post('/api/admin/domain/update-contacts', {
                id,
                contacts,
                contact_template_ids: templateIds
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 续费域名（Admin）
     * @param {number} id - 域名ID
     * @param {number} years - 续费年数
     * @returns {Promise} 续费结果
     */
    renewDomain: async (id, years) => {
        try {
            const response = await apiClient.post('/api/admin/domain/renew', {
                id,
                years
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // 域名生命周期管理（Admin Only）
    // ==========================================

    /**
     * Hold域名（Admin）
     * @param {number} id - 域名ID
     * @param {string} reason - 原因
     * @returns {Promise} 操作结果
     */
    holdDomain: async (id, reason = '') => {
        try {
            const response = await apiClient.post('/api/admin/domains/hold', { id, reason });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 解除Hold（Admin）
     * @param {number} id - 域名ID
     * @returns {Promise} 操作结果
     */
    unholdDomain: async (id) => {
        try {
            const response = await apiClient.post('/api/admin/domains/unhold', { id });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 锁定域名转移（Admin）
     * @param {number} id - 域名ID
     * @returns {Promise} 操作结果
     */
    lockTransfer: async (id) => {
        try {
            const response = await apiClient.post('/api/admin/domains/lock-transfer', { id });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 解锁域名转移（Admin）
     * @param {number} id - 域名ID
     * @returns {Promise} 操作结果
     */
    unlockTransfer: async (id) => {
        try {
            const response = await apiClient.post('/api/admin/domains/unlock-transfer', { id });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 转移域名所有权（Admin）
     * @param {number} id - 域名ID
     * @param {number} newUserId - 新用户ID
     * @returns {Promise} 操作结果
     */
    changeDomainOwner: async (id, newUserId) => {
        try {
            const response = await apiClient.post('/api/admin/domains/change-owner', { id, new_user_id: newUserId });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 延长域名到期时间（Admin）
     * @param {number} id - 域名ID
     * @param {number} days - 延长天数
     * @param {boolean} free - 是否免费延期
     * @returns {Promise} 操作结果
     */
    extendDomain: async (id, days, free = false) => {
        try {
            const response = await apiClient.post('/api/admin/domains/extend', { id, days, free });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 暂停域名服务（Admin）
     * @param {number} id - 域名ID
     * @param {string} reason - 原因
     * @returns {Promise} 操作结果
     */
    suspendDomain: async (id, reason = '') => {
        try {
            const response = await apiClient.post('/api/admin/domains/suspend', { id, reason });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 恢复域名服务（Admin）
     * @param {number} id - 域名ID
     * @returns {Promise} 操作结果
     */
    resumeDomain: async (id) => {
        try {
            const response = await apiClient.post('/api/admin/domains/resume', { id });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取域名授权码（Admin）
     * @param {number} id - 域名ID
     * @returns {Promise} 授权码
     */
    getDomainAuthCode: async (id) => {
        try {
            const response = await apiClient.post('/api/admin/domains/get-auth-code', { id });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // 联系人模板管理（User）
    // ==========================================

    /**
     * 获取联系人模板列表（Admin：按域名所有者过滤）
     * @param {number} [userId] - 域名所有者用户ID；不传则返回全部
     * @returns {Promise} 模板列表
     */
    getContactTemplates: async (userId) => {
        try {
            const params = userId ? { user_id: userId } : {};
            const response = await apiClient.get('/api/admin/domain-contacts', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取单个联系人模板
     * @param {number} id - 模板ID
     * @returns {Promise} 模板详细信息
     */
    getContactTemplate: async (id) => {
        try {
            const response = await apiClient.get('/api/user/domain-contacts/get', {
                params: { id }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取默认联系人模板
     * @returns {Promise} 默认模板
     */
    getDefaultContactTemplate: async () => {
        try {
            const response = await apiClient.get('/api/user/domain-contacts/default');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 创建联系人模板
     * @param {Object} data - 模板数据
     * @returns {Promise} 创建结果
     */
    createContactTemplate: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-contacts/create', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 更新联系人模板
     * @param {number} id - 模板ID
     * @param {Object} data - 模板数据
     * @returns {Promise} 更新结果
     */
    updateContactTemplate: async (id, data) => {
        try {
            const response = await apiClient.post('/api/user/domain-contacts/update', {
                id,
                ...data
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 删除联系人模板
     * @param {number} id - 模板ID
     * @returns {Promise} 删除结果
     */
    deleteContactTemplate: async (id) => {
        try {
            const response = await apiClient.delete('/api/user/domain-contacts/delete', {
                data: { id }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 设置默认联系人模板
     * @param {number} id - 模板ID
     * @returns {Promise} 设置结果
     */
    setDefaultContactTemplate: async (id) => {
        try {
            const response = await apiClient.post('/api/user/domain-contacts/set-default', { id });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // 联系人模板管理（Admin）
    // ==========================================

    /**
     * 获取所有联系人模板列表（Admin）
     * @param {Object} params - 查询参数
     * @returns {Promise} 模板列表
     */
    getAdminContactTemplates: async (params = {}) => {
        try {
            const response = await apiClient.get('/api/admin/domain-contacts', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // Admin sync / health endpoints
    // ==========================================

    /**
     * 已存在的注册商连接测试（按已保存的注册商ID）。
     * 与 testConnection(pluginName, credentials, sandboxMode) 不同 —
     * 这个不需要外面把凭据再传一遍，直接复用已存的。
     * 内部会复用 /api/admin/domain/registrars/test-connection。
     */
    testConnectionForRegistrar: async (registrarId) => {
        try {
            // getGet 只回显掩码凭证;按 id 测试,后端用库里原值。
            const resp = await apiClient.post('/api/admin/domain/registrars/test-connection', {
                id: registrarId,
                credentials: {},
            });
            return resp.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 同步队列状态 / 健康
     */
    getSyncQueue: async () => {
        try {
            const response = await apiClient.get('/api/admin/domain/sync-queue');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 同步统计（today/week/month）
     */
    getSyncStatistics: async (period = 'today') => {
        try {
            const response = await apiClient.get('/api/admin/domain/sync-statistics', {
                params: { period }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 单个域名的同步日志
     */
    getDomainSyncLogs: async (domainId, params = {}) => {
        try {
            const response = await apiClient.get('/api/admin/domains/sync-logs', {
                params: { domain_id: domainId, ...params }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 触发单个域名同步
     */
    triggerDomainSync: async (domainId) => {
        try {
            const response = await apiClient.post('/api/admin/domains/trigger-sync', {
                domain_id: domainId
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 触发批量同步（按 domain_ids 或筛选）
     */
    triggerBatchSync: async (payload) => {
        try {
            const response = await apiClient.post('/api/admin/domain/trigger-batch-sync', payload);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取插件能力清单（前端用于在注册商列表展示）
     * 复用 getPlugins() —— 这里只是个语义化别名。
     */
    getPluginCapabilities: async () => {
        try {
            const response = await apiClient.get('/api/admin/domain/registrars/plugins');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取单个联系人模板（Admin）
     * @param {number} id - 模板ID
     * @returns {Promise} 模板详细信息
     */
    getAdminContactTemplate: async (id) => {
        try {
            const response = await apiClient.get('/api/admin/domain-contacts/get', {
                params: { id }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },
};

export default registrarService;
